/**
 * OCPI Settings Store
 *
 * Zustand store for OCPI settings state management.
 * Follows Clean Architecture pattern with lazy service access.
 */

import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import type { ConnectionStatus, PublishLocationsResult, PublishTariffsResult } from '../types/connection-status-types';
import type { ExternalCpoConnectionRequestDto, VpostConnectionRequestDto, CpoType } from '../types/api-types';
import type { OcpiModule } from '../use-cases/SyncCpoOcpiDataUseCase';
import { getConnectionCheckErrorMessage, getHttpStatus } from '../utils/error-messages';
import type { CpoSyncOutcome } from '../types/api-types';

/**
 * Result of a publish/sync action, returned to the caller for its alert.
 * Returned rather than stored, so parallel actions don't overwrite each other's result.
 */
export type ActionOutcome<T> =
  | { ok: true; data: T; error?: undefined; status?: undefined }
  | { ok: false; data?: undefined; error: string; status: number | null };

interface OcpiSettingsStore {
  // Data
  connectionStatus: ConnectionStatus | null;
  publishLocationsResult: PublishLocationsResult | null;
  publishTariffsResult: PublishTariffsResult | null;
  syncEvesSuccess: boolean | null;
  /** CPO type: internal (Nayax manages) or external (operator's own OCPI system) */
  cpoType: CpoType | null;

  // Loading states
  isLoadingStatus: boolean;
  isRefreshing: boolean;
  isPublishingLocations: boolean;
  isPublishingTariffs: boolean;
  isSyncingEves: boolean;
  isSettingUpKiosk: boolean;
  isOnboarding: boolean;
  isSettingUpExternalCpo: boolean;
  isSettingUpVpost: boolean;
  /** Keys of the form "connectionId:module" that are currently syncing */
  syncingCpoModules: Set<string>;

  // Error states
  error: string | null;

  // Flags
  needsOnboarding: boolean;

  // Metadata
  lastChecked: Date | null;

  // Actions
  checkConnectionStatus: (operatorId: number, operatorName: string) => Promise<void>;
  publishLocations: (operatorId: number) => Promise<ActionOutcome<PublishLocationsResult>>;
  publishTariffs: (operatorId: number) => Promise<ActionOutcome<PublishTariffsResult>>;
  syncEves: (operatorId: number, publishToOcpi?: boolean) => Promise<ActionOutcome<void>>;
  setupKioskConnection: (operatorId: number, operatorName: string) => Promise<void>;
  onboardOperator: (operatorId: number, partyId: string, countryCode: string, name: string) => Promise<boolean>;
  setupExternalCpoConnection: (operatorId: number, operatorName: string, data: ExternalCpoConnectionRequestDto) => Promise<boolean>;
  setupVpostConnection: (operatorId: number, operatorName: string, data: VpostConnectionRequestDto) => Promise<boolean>;
  loadCsmsNames: () => Promise<string[]>;
  loadKioskCsmsNames: () => Promise<string[]>;
  syncCpoOcpiData: (operatorId: number, connectionId: number, ocpiModule: OcpiModule) => Promise<ActionOutcome<CpoSyncOutcome>>;
  clearPublishResults: () => void;
  clearLocationsResult: () => void;
  clearTariffsResult: () => void;
  clearSyncEvesResult: () => void;
  clearError: () => void;
  reset: () => void;
}

export const useOcpiSettingsStore = create<OcpiSettingsStore>((set) => ({
  // Initial state
  connectionStatus: null,
  publishLocationsResult: null,
  publishTariffsResult: null,
  syncEvesSuccess: null,
  cpoType: null,
  isLoadingStatus: false,
  isRefreshing: false,
  isPublishingLocations: false,
  isPublishingTariffs: false,
  isSyncingEves: false,
  isSettingUpKiosk: false,
  isOnboarding: false,
  isSettingUpExternalCpo: false,
  isSettingUpVpost: false,
  syncingCpoModules: new Set<string>(),
  error: null,
  needsOnboarding: false,
  lastChecked: null,

  /**
   * Check connection status across all OCPI services
   * Also detects if operator is Internal or External CPO
   */
  checkConnectionStatus: async (operatorId: number, operatorName: string) => {
    // Clear all results when operator changes (publish locations, tariffs, sync data)
    set({
      isLoadingStatus: true,
      error: null,
      needsOnboarding: false,
      publishLocationsResult: null,
      publishTariffsResult: null,
      syncEvesSuccess: null,
    });

    try {
      // Lazy service access inside action
      const services = servicesContainer.getServices();

      // 15s timeout — if /ocpi-overview hangs, surface an error instead of loading forever
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Connection status check timed out. Please try again.')), 15000)
      );

      // Execute use case - now returns cpoType as well
      const status = await Promise.race([
        services.checkConnectionStatusUseCase.execute(operatorId, operatorName),
        timeout,
      ]);

      // If cpoType is null, operator is not configured and needs onboarding
      if (status.cpoType === null) {
        set({
          connectionStatus: null,
          cpoType: null,
          isLoadingStatus: false,
          error: null,
          needsOnboarding: true,
        });
        return;
      }

      // Operator exists and is configured (internal or external)
      set({
        connectionStatus: status,
        cpoType: status.cpoType,
        isLoadingStatus: false,
        lastChecked: new Date(),
        error: null,
        needsOnboarding: false,
      });
    } catch (error) {
      const errorMessage = getConnectionCheckErrorMessage(error);
      const lowerMessage = errorMessage.toLowerCase();

      // Check if operator exists in Infuse (has Infuse operator ID in error message)
      // Pattern matches: "Infuse operator ID - 1283", "Infuse operator ID: 1283", etc.
      // If this is present, operator EXISTS but may have no subscriptions/connections
      const hasInfuseOperator = /infuse operator id\s*[-:]\s*\d+/i.test(errorMessage);

      // Check if error explicitly says no Infuse operator exists
      // "No Infuse operator id corresponding" means operator truly doesn't exist
      const noInfuseOperator = lowerMessage.includes('no infuse operator') ||
                               lowerMessage.includes('no infuse operator id corresponding');

      // Check if this is a 404 error (operator not found / not configured)
      // 404 means the operator doesn't exist in OCPI system and needs onboarding
      const is404Error = lowerMessage.includes('404') ||
                         lowerMessage.includes('not found') ||
                         lowerMessage.includes('resource not found');

      // Needs onboarding if:
      // 1. Error says "no infuse operator" AND does NOT contain an Infuse operator ID, OR
      // 2. It's a 404 error (operator not configured) AND does NOT contain an Infuse operator ID
      // If hasInfuseOperator is true, operator exists but has other issues (no subscriptions, etc.)
      const requiresOnboarding = (noInfuseOperator || is404Error) && !hasInfuseOperator;

      // Clear connection status on error to prevent stale data from previous operator
      set({
        connectionStatus: null,
        cpoType: null,
        isLoadingStatus: false,
        error: requiresOnboarding ? null : errorMessage,
        needsOnboarding: requiresOnboarding,
      });

      if (!requiresOnboarding) {
        console.error('Failed to check connection status:', error);
      }
    }
  },

  /**
   * Publish locations to OCPI network
   */
  publishLocations: async (operatorId: number) => {
    set({ isPublishingLocations: true, error: null, publishLocationsResult: null });

    try {
      const services = servicesContainer.getServices();
      const result = await services.publishLocationsUseCase.execute(operatorId);

      set({
        publishLocationsResult: result,
        isPublishingLocations: false,
        error: result.isSuccess ? null : result.errorSummary || 'Publish failed',
      });

      return { ok: true, data: result };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to publish locations';

      set({
        isPublishingLocations: false,
        error: errorMessage,
      });

      console.error('Failed to publish locations:', error);
      return { ok: false, error: errorMessage, status: getHttpStatus(error) };
    }
  },

  /**
   * Publish tariffs to OCPI network
   */
  publishTariffs: async (operatorId: number) => {
    set({ isPublishingTariffs: true, error: null, publishTariffsResult: null });

    try {
      const services = servicesContainer.getServices();
      const result = await services.publishTariffsUseCase.execute(operatorId);

      set({
        publishTariffsResult: result,
        isPublishingTariffs: false,
        error: result.isSuccess ? null : result.errorSummary || 'Publish failed',
      });

      return { ok: true, data: result };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to publish tariffs';

      set({
        isPublishingTariffs: false,
        error: errorMessage,
      });

      console.error('Failed to publish tariffs:', error);
      return { ok: false, error: errorMessage, status: getHttpStatus(error) };
    }
  },

  /**
   * Sync EVSEs (Electric Vehicle Supply Equipment) data
   * Synchronizes EVSE data for the operator and optionally publishes to OCPI
   * @param operatorId - The operator ID to sync
   * @param publishToOcpi - Whether to publish to OCPI after sync (default: true)
   * @returns ok on success, or the failure reason and HTTP status
   */
  syncEves: async (operatorId: number, publishToOcpi: boolean = true) => {
    set({ isSyncingEves: true, error: null, syncEvesSuccess: null });

    try {
      const services = servicesContainer.getServices();
      await services.syncEvesUseCase.execute(operatorId, publishToOcpi);

      set({
        syncEvesSuccess: true,
        isSyncingEves: false,
        error: null,
      });

      return { ok: true, data: undefined };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to sync EVSEs';

      set({
        syncEvesSuccess: false,
        isSyncingEves: false,
        error: errorMessage,
      });

      console.error('Failed to sync EVSEs:', error);
      return { ok: false, error: errorMessage, status: getHttpStatus(error) };
    }
  },

  /**
   * Setup Kiosk connection for operator
   */
  setupKioskConnection: async (operatorId: number, operatorName: string) => {
    set({ isSettingUpKiosk: true, error: null });

    try {
      const services = servicesContainer.getServices();
      await services.setupKioskConnectionUseCase.execute(operatorId);

      // Re-fetch connection status after setup
      const status = await services.checkConnectionStatusUseCase.execute(operatorId, operatorName);

      set({
        connectionStatus: status,
        isSettingUpKiosk: false,
        lastChecked: new Date(),
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to setup Kiosk connection';

      set({
        isSettingUpKiosk: false,
        error: errorMessage,
      });

      console.error('Failed to setup Kiosk connection:', error);
    }
  },

  /**
   * Onboard operator to OCPI by creating CPO party configuration
   * Returns true if onboarding was successful
   */
  onboardOperator: async (operatorId: number, partyId: string, countryCode: string, name: string) => {
    set({ isOnboarding: true, error: null });

    try {
      const services = servicesContainer.getServices();
      await services.onboardOperatorUseCase.execute(operatorId, partyId, countryCode, name);

      // After successful onboarding, refresh connection status
      const status = await services.checkConnectionStatusUseCase.execute(operatorId, name);

      set({
        connectionStatus: status,
        cpoType: 'internal', // Internal CPO after onboarding with party info
        isOnboarding: false,
        needsOnboarding: false,
        lastChecked: new Date(),
        error: null,
      });

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to onboard operator';

      set({
        isOnboarding: false,
        error: errorMessage,
      });

      console.error('Failed to onboard operator:', error);
      return false;
    }
  },

  /**
   * Setup external CPO connection for an operator
   * This is used during onboarding when the operator chooses External CPO type
   * Returns true if setup was successful
   */
  setupExternalCpoConnection: async (
    operatorId: number,
    operatorName: string,
    data: ExternalCpoConnectionRequestDto
  ) => {
    set({ isSettingUpExternalCpo: true, error: null });

    try {
      const services = servicesContainer.getServices();
      await services.setupExternalCpoConnectionUseCase.execute(operatorId, data);

      // After successful setup, refresh connection status
      const status = await services.checkConnectionStatusUseCase.execute(operatorId, operatorName);

      set({
        connectionStatus: status,
        cpoType: 'external', // External CPO after setup
        isSettingUpExternalCpo: false,
        needsOnboarding: false,
        lastChecked: new Date(),
        error: null,
      });

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to setup external CPO connection';

      set({
        isSettingUpExternalCpo: false,
        error: errorMessage,
      });

      console.error('Failed to setup external CPO connection:', error);
      return false;
    }
  },

  /**
   * Setup OCPI-to-VPOST connection for an external CPO operator
   * Returns true if setup was successful
   */
  setupVpostConnection: async (
    operatorId: number,
    operatorName: string,
    data: VpostConnectionRequestDto
  ) => {
    set({ isSettingUpVpost: true, error: null });

    try {
      const services = servicesContainer.getServices();
      await services.setupVpostConnectionUseCase.execute(operatorId, data);

      // After successful setup, refresh connection status
      const status = await services.checkConnectionStatusUseCase.execute(operatorId, operatorName);

      set({
        connectionStatus: status,
        cpoType: 'external',
        isSettingUpVpost: false,
        needsOnboarding: false,
        lastChecked: new Date(),
        error: null,
      });

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to setup OCPI-to-VPOST connection';

      set({
        isSettingUpVpost: false,
        error: errorMessage,
      });

      console.error('Failed to setup VPOST connection:', error);

      // Still refresh — backend may have partially created the connection
      // (e.g. 500 on settings insert after OCPI handshake succeeded).
      // The dashboard's useEffect will auto-close the form if connection appears.
      try {
        const refreshServices = servicesContainer.getServices();
        const status = await refreshServices.checkConnectionStatusUseCase.execute(operatorId, operatorName);
        set({ connectionStatus: status, cpoType: 'external', lastChecked: new Date() });
      } catch {
        // ignore — error state already set above
      }

      return false;
    }
  },

  /**
   * Load available CSMS names for OCPI-to-VPOST connection setup.
   * Data-lookup action — returns result directly to caller.
   */
  loadCsmsNames: async () => {
    try {
      const services = servicesContainer.getServices();
      return await services.getCsmsNamesUseCase.execute();
    } catch (error) {
      console.error('Failed to load CSMS names:', error);
      throw error;
    }
  },

  /**
   * Load available CSMS names for external Kiosk connection setup.
   * Data-lookup action — returns result directly to caller.
   */
  loadKioskCsmsNames: async () => {
    try {
      const services = servicesContainer.getServices();
      return await services.getKioskCsmsNamesUseCase.execute();
    } catch (error) {
      console.error('Failed to load Kiosk CSMS names:', error);
      throw error;
    }
  },

  /**
   * Sync latest CPO OCPI data for a specific connection and module (External CPO only)
   * Incremental sync with backend retries (max attempts from the backend's appsettings) and full-sync fallback.
   * Returns the backend outcome (attempts, fell back) on success, or the failure reason and HTTP status.
   */
  syncCpoOcpiData: async (operatorId: number, connectionId: number, ocpiModule: OcpiModule) => {
    const key = `${connectionId}:${ocpiModule}`;
    set((state) => ({ syncingCpoModules: new Set([...state.syncingCpoModules, key]) }));

    try {
      const services = servicesContainer.getServices();
      const outcome = await services.syncCpoOcpiDataUseCase.execute(operatorId, connectionId, ocpiModule);

      set((state) => {
        const next = new Set(state.syncingCpoModules);
        next.delete(key);
        return { syncingCpoModules: next };
      });

      return { ok: true, data: outcome };
    } catch (error) {
      set((state) => {
        const next = new Set(state.syncingCpoModules);
        next.delete(key);
        return { syncingCpoModules: next };
      });

      console.error('Failed to sync CPO OCPI data:', error);
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'Failed to sync CPO OCPI data',
        status: getHttpStatus(error),
      };
    }
  },

  /**
   * Clear publish results
   */
  clearPublishResults: () => {
    set({
      publishLocationsResult: null,
      publishTariffsResult: null,
    });
  },

  /**
   * Clear locations result only
   */
  clearLocationsResult: () => {
    set({ publishLocationsResult: null });
  },

  /**
   * Clear tariffs result only
   */
  clearTariffsResult: () => {
    set({ publishTariffsResult: null });
  },

  /**
   * Clear sync EVEs result only
   */
  clearSyncEvesResult: () => {
    set({ syncEvesSuccess: null });
  },

  /**
   * Clear error message
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set({
      connectionStatus: null,
      publishLocationsResult: null,
      publishTariffsResult: null,
      syncEvesSuccess: null,
      cpoType: null,
      isLoadingStatus: false,
      isRefreshing: false,
      isPublishingLocations: false,
      isPublishingTariffs: false,
      isSyncingEves: false,
      isSettingUpKiosk: false,
      isOnboarding: false,
      isSettingUpExternalCpo: false,
      isSettingUpVpost: false,
      syncingCpoModules: new Set<string>(),
      error: null,
      needsOnboarding: false,
      lastChecked: null,
    });
  },
}));
